import { useEvalFeatureFlagsQuery } from "src/store/features/api/notabene";
import { useAuth } from "src/providers/AuthProvider";

/**
 * Travel-Rule master kill-switch hook. Returns `true` iff `TR_ENABLED` AND
 * the per-direction flag (`TR_OUTBOUND_ENABLED` / `TR_INBOUND_ENABLED`) both
 * evaluate truthy for the current user.
 *
 * Both flags are batched into a single `/feature-flags/eval` round-trip via
 * RTK Query's request deduplication, so calling this hook on multiple
 * components in the same page costs one network call.
 *
 * Returns `false` while loading or on error — pre-launch we'd rather hide
 * a TR surface than render one whose backend would refuse the call.
 *
 * Skipped entirely while `isAuthenticated` is false. The endpoint requires a
 * Transmit session (`AuthnMiddleware` rejects 401 otherwise), and with the
 * RTK base config setting `refetchOnFocus: true`, this hook would otherwise
 * fire whenever a tab regains focus — including the post-logout transient
 * and the stale-token state when a session has lapsed in the background.
 *
 * Usage:
 *   const trOn = useTrEnabled("OUTBOUND");
 *   if (!trOn) return null;
 */
export function useTrEnabled(direction: "OUTBOUND" | "INBOUND"): boolean {
  const { isAuthenticated } = useAuth();
  const directionKey =
    direction === "OUTBOUND" ? "TR_OUTBOUND_ENABLED" : "TR_INBOUND_ENABLED";
  const { data, isLoading, isError } = useEvalFeatureFlagsQuery(
    ["TR_ENABLED", directionKey],
    { skip: !isAuthenticated },
  );
  if (isLoading || isError || !data) return false;
  return Boolean(data["TR_ENABLED"]) && Boolean(data[directionKey]);
}
